/**
 * This PromotionalCard is used as a single item for the Promotional Rail component.
 *
 * @module views/components/PromotionalCard
 * @memberof -Common
 */
import './PromotionalCard.scss';

import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import UltaAsset from '@ulta/core/components/UltaAsset/UltaAsset';
import { spreadProps } from '@ulta/core/utils/dom/dom';

/**
 * Represents a PromotionalCard component
 *
 * @method
 * @param {PromotionalCardProps} props - React properties passed from composition
 * @returns PromotionalCard
 */
export const PromotionalCard = function( props ){
  const {
    alignment,
    backgroundColor,
    disclaimer,
    highlight,
    image,
    linkAction,
    listPrice,
    salePrice,
    supportingText,
    title
  } = props;

  const salePriceRange = salePrice?.includes( '-' ) || salePrice?.includes( 'Only' ) || listPrice?.includes( '-' );
  const salesPriceRangeClass = ( salePriceRange ? 'PromotionalCard__priceRange' : 'PromotionalCard__pricing' );
  const salesPriceTextStyle = ( salePrice?.includes( '-' ) ? 'body-2' : 'title-6' );
  const listPriceTextStyle = ( listPrice?.includes( '-' ) ? 'body-2' : 'body-3' );

  const cardContent = () => {
    return (
      <div className='PromotionalCard__mainContainer'>
        { image &&
        <div className={ `PromotionalCard__image` }>
          <UltaAsset
            imageUrl={ image.imageUrl }
            { ...spreadProps( {
              metaData: image.metaData
            } ) }
          />
        </div>
        }
        <div
          className={
            classNames( 'PromotionalCard__description' )
          }
        >
          { highlight &&
            <div className={ `PromotionalCard__highlight` }>
              <Text
                textStyle='body-3'
              >
                { highlight }
              </Text>
            </div>
          }

          { title &&
          <div className={ `PromotionalCard__title` }>
            <Text
              textStyle='title-6'
            >
              { title }
            </Text>
          </div>
          }

          { supportingText &&
          <div className={ `PromotionalCard__supportingText` }>
            <Text
              textStyle='body-2'
            >
              { supportingText }
            </Text>
          </div>
          }

          { disclaimer &&
          <div className={ `PromotionalCard__disclaimer` }>
            <Text
              textStyle='body-3'
              color='neutral-700'
            >
              { disclaimer }
            </Text>
          </div>
          }
          <div className={ salesPriceRangeClass }>
            { salePrice &&
              <div className={ `PromotionalCard__salePrice` }>
                <Text
                  textStyle={ salesPriceTextStyle }
                  color='magenta-500'
                >
                  { salePrice }
                </Text>
              </div>
            }
            { listPrice &&
              <div className={ `PromotionalCard__listPrice` }>
                <Text
                  textStyle={ listPriceTextStyle }
                  color='neutral-700'
                >
                  { listPrice }
                </Text>
              </div>
            }
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={
        classNames( 'PromotionalCard', {
          [`PromotionalCard--${alignment}`]: alignment,
          [`PromotionalCard--${backgroundColor}`]: backgroundColor
        } )
      }
    >
      { linkAction &&
        <Link_Huge action={ linkAction } >
          { cardContent( ) }
        </Link_Huge>
      }
      { !linkAction &&
        <>
          { cardContent( ) }
        </>
      }
    </div>
  );
};

/**
 * Property type definitions
 *
 * @typedef PromotionalCardProps
 * @type {object}
 * @property {string} alignment - sets the alignment for PromotionalCard component
 * @property {string} backgroundColor - Sets the backgroundColor for card
 * @property {string} disclaimer - Sets the disclaimer
 * @property {string} highlight - Sets the highlight
 * @property {object} image - Sets the image for promotional card
 * @property {object} linkAction - Sets the linkAction
 * @property {string} listPrice - Sets the listPrice
 * @property {string} salePrice - Sets the salePrice
 * @property {string} supportingText - Sets the titleSupport
 * @property {string} title - Sets the title
 */
export const propTypes =  {
  alignment: PropTypes.oneOf( ['top', 'bottom', 'vertical'] ),
  backgroundColor: PropTypes.string,
  disclaimer: PropTypes.string,
  highlight: PropTypes.string,
  image: PropTypes.object,
  linkAction: PropTypes.object,
  listPrice: PropTypes.string,
  salePrice: PropTypes.string,
  supportingText: PropTypes.string,
  title: PropTypes.string
};

PromotionalCard.propTypes = propTypes;

export default PromotionalCard;
